import { number, string, isnil } from 'vue-mc/validation'
import BaseModel from '@/models/BaseModel'

export default class Order extends BaseModel {
  defaults () {
    return {
      _id: null,
      provider: null,
      status: null,
      amount: 0
    }
  }

  options () {
    return {
      identifier: '_id',
      methods: {
        send: 'POST',
        confirm: 'POST',
        localConfirm: 'POST',
        pay: 'POST',
        bill: 'POST',
        receive: 'POST',
        cancel: 'POST',
        refund: 'POST',
        detail: 'GET',
        receiveWithCode: 'POST',
        receiveWithoutInvoice: 'POST',
        attachInvoice: 'POST',
        refreshProducts: 'POST',
        checkStatus: 'GET'
      }
    }
  }

  getDefaultMethods () {
    return {
      fetch: 'GET',
      update: 'PUT',
      create: 'POST',
      patch: 'PUT',
      delete: 'POST'
    }
  }

  validation () {
    return {
      provider: [isnil.or(string)],
      status: [isnil.or(string)],
      amount: [number]
    }
  }

  checkStatus (invoiceId) {
    let method = this.getOption('methods.checkStatus')
    let route = this.getRoute('checkStatus')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    url += `/${invoiceId}/check-status`

    return this.createRequest({ method, url, headers }).send()
  }

  refreshProducts (productId) {
    let method = this.getOption('methods.refreshProducts')
    let route = this.getRoute('refreshProducts')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    url += `/${productId}`

    return this.createRequest({ method, url, headers }).send()
  }

  update (data) {
    let method = this.getOption('methods.update')
    let route = this.getRoute('update')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers, data }).send()
  }

  create (data) {
    let method = this.getOption('methods.create')
    let route = this.getRoute('create')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers, data }).send()
  }

  send (data) {
    let method = this.getOption('methods.send')
    let route = this.getRoute('send')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers, data }).send()
  }

  confirm (data) {
    let method = this.getOption('methods.confirm')
    let route = this.getRoute('confirm')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers, data }).send()
  }

  localConfirm (data) {
    let method = this.getOption('methods.localConfirm')
    let route = this.getRoute('localConfirm')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers, data }).send()
  }

  pay (data) {
    let method = this.getOption('methods.pay')
    let route = this.getRoute('pay')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers, data }).send()
  }

  bill (data) {
    let method = this.getOption('methods.bill')
    let route = this.getRoute('bill')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers, data }).send()
  }

  receive (data) {
    let method = this.getOption('methods.receive')
    let route = this.getRoute('receive')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.sendRequest({ method, url, headers, data })
  }

  receiveWithCode (data) {
    let method = this.getOption('methods.receive')
    let route = this.getRoute('receiveWithCode')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.sendRequest({ method, url, headers, data })
  }

  cancel (data) {
    let method = this.getOption('methods.cancel')
    let route = this.getRoute('cancel')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers, data }).send()
  }

  refund (data) {
    let method = this.getOption('methods.refund')
    let route = this.getRoute('refund')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers, data }).send()
  }

  detail () {
    let method = this.getOption('methods.detail')
    let route = this.getRoute('detail')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers }).send()
  }

  fetch (data = {}) {
    let method = this.getOption('methods.fetch')
    let route = this.getRoute('fetch')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    const query = Object.entries(data).reduce((acc, num, index) => {
      acc += index > 0 ? '&' : ''
      acc += `${num[0]}=${num[1]}`
      return acc
    }, '')

    url = query ? `${url}?${query}` : url
    return this.createRequest({ method, url, headers }).send()
  }

  inbox (data = {}) {
    let method = this.getOption('methods.fetch')
    let route = this.getRoute('inbox')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    const query = Object.entries(data).reduce((acc, num, index) => {
      acc += index > 0 ? '&' : ''
      acc += `${num[0]}=${num[1]}`
      return acc
    }, '')

    url = query ? `${url}?${query}` : url
    return this.createRequest({ method, url, headers }).send()
  }

  receiveWithoutInvoice (data) {
    let method = this.getOption('methods.receiveWithoutInvoice')
    let route = this.getRoute('receiveWithoutInvoice')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.sendRequest({ method, url, headers, data })
  }

  attachInvoice (data) {
    let method = this.getOption('methods.attachInvoice')
    let route = this.getRoute('attachInvoice')
    let params = this.getRouteParameters()
    let headers = this.getDefaultHeaders()
    let url = this.getURL(route, params)

    return this.createRequest({ method, url, headers, data }).send()
  }

  shouldPatch () {
    return true
  }

  routes () {
    return {
      list: `${this.API_BASE}/v1/orders`,
      fetch: `${this.API_BASE}/v1/orders`,
      inbox: `${this.API_BASE}/v1/orders/inbox/provider`,
      send: `${this.API_BASE}/v1/orders/{_id}/send`,
      confirm: `${this.API_BASE}/v1/orders/{_id}/confirm`,
      localConfirm: `${this.API_BASE}/v1/orders/{_id}/local-confirm`,
      pay: `${this.API_BASE}/v1/orders/{_id}/pay`,
      bill: `${this.API_BASE}/v1/orders/{_id}/bill`,
      receive: `${this.API_BASE}/v1/orders/{_id}/receive`,
      cancel: `${this.API_BASE}/v1/orders/{_id}/cancel`,
      refund: `${this.API_BASE}/v1/orders/{_id}/refund`,
      create: `${this.API_BASE}/v1/orders`,
      detail: `${this.API_BASE}/v1/orders/{_id}`,
      update: `${this.API_BASE}/v1/orders/{_id}`,
      receiveWithCode: `${this.API_BASE}/v1/orders/{_id}/receive-with-code`,
      receiveWithoutInvoice: `${this.API_BASE}/v1/orders/{_id}/receive-without-invoice`,
      attachInvoice: `${this.API_BASE}/v1/orders/{_id}/attach-invoice`,
      refreshProducts: `${this.API_BASE}/v1/orders/{_id}/refresh-products`,
      checkStatus: `${this.API_BASE}/v1/orders/{_id}/invoices`
    }
  }
}
